import { useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Marker, Pane, Popup } from "react-leaflet";
import { divIcon } from "leaflet";
import _ from "lodash";

import { Box, Button, Card, Stack, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ForkLeftOutlinedIcon from "@mui/icons-material/ForkLeftOutlined";

import { STATIONS_COLOR, STATIONS_SIZE } from "Configs/map";
import QueryData from "Constants/QueryData";
import { STATIONS } from "Constants/Stations";
import { OperationNames } from "Types/operation";
import { IStation } from "Types/station";

import { useAppDispatch, useAppSelector } from "Hooks/useRedux";
import useStations from "Hooks/useStations";

import { changeModal } from "Redux/modalSlice";
import { selectStation } from "Redux/stationsSlice";
import { setTooltip } from "Redux/tooltipSlice";

export default function Stations({ minimize = false }: { minimize?: Boolean }) {
  const dispatch = useAppDispatch();
  const { type, name } = useAppSelector((state) => state.operation);
  const { ids, visible } = useAppSelector((state) => state.stations);
  const { visibility } = useAppSelector((state) => state.filters);
  const { stationLayer } = useAppSelector((state) => state.layers);
  const { visibleType, singleStationData } = useAppSelector((state) => state.singleLocation);

  const { data } = useStations();

  let stations: IStation[];
  if (minimize) {
    stations = data?.filter((station: IStation) => ids.includes(station.id));
  } else {
    stations = visible ? data : data?.filter((station: IStation) => ids.includes(station.id));
  }

  const handleClickStation = (station: IStation) => {
    const operationNames: OperationNames[] = ["create-path", "edit-path"];
    if (name !== null && operationNames.includes(name)) {
      dispatch(selectStation(station));
    }
  };
  const editStation = (station: IStation) => {
    dispatch(changeModal({ status: "add-station", data: { ...station, isEdit: true } }));
  };

  const stationsMarker = useMemo(() => {
    if (!data && singleStationData.length === 0) return null;
    let finalStations: IStation[] = [];
    if (visible) {
      const filteredWithVisibility =
        visibility.length > 0 ? stations.filter((station: IStation) => visibility.includes(station.type)) : stations;
      const filteredWithType = stations.filter((station: IStation) => station.type === type);
      finalStations = type ? filteredWithType : filteredWithVisibility || [];
    } else if (visibleType === "station") {
      finalStations = singleStationData;
    }
    return finalStations.map((station) => {
      const stationColor = STATIONS_COLOR[station.type];
      const stationData = STATIONS.filter((i) => i.type === station.type)[0];

      const iconMarkup = renderToStaticMarkup(
        <Box
          style={{
            width: `${STATIONS_SIZE}px`,
            height: `${STATIONS_SIZE}px`,
            backgroundColor: stationColor,
            borderRadius: "50%",
            border: "4px solid #F5F5F5",
            display: "grid",
            placeItems: "center",
            color: "#fff",
            filter: ids.includes(station.id) ? "drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))" : "",
          }}
        >
          {stationData.icon}
        </Box>
      );
      const icon = divIcon({
        html: iconMarkup,
        iconAnchor: [STATIONS_SIZE / 2, STATIONS_SIZE / 2],
      });

      return (
        <Marker
          key={station.id}
          position={[station.lat, station.lng]}
          icon={icon}
          zIndexOffset={1}
          eventHandlers={{
            click: () => handleClickStation(station),
            mouseover: () => {
              dispatch(
                setTooltip({
                  text: `ایستگاه ${station.title} - ${station.lat}/${station.lng} - کد ${station.code || "نامشخص"} - آدرس ${
                    station.address || "نامخشص"
                  }`,
                })
              );
            },
            mouseout: () => {
              dispatch(setTooltip({ text: "" }));
            },
          }}
        >
          {type === null && (
            <Popup interactive closeButton={false} offset={[0, -30]}>
              <Card sx={{ bgcolor: "#EDEEF0" }}>
                <Card sx={{ p: 1.5, width: "300px", boxShadow: 0 }}>
                  <Stack direction="row-reverse" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight="800" sx={{ fontSize: "18px !important", mb: "0 !important" }}>
                      {station.title}
                    </Typography>

                    <div>
                      {/* <Button variant="contained" sx={{ minWidth: "50px", width: "50px", height: "50px", ml: 0.5 }}>
                        <ForkLeftOutlinedIcon sx={{ position: "relative !important" }} />
                      </Button> */}
                      <Button
                        variant="outlined"
                        sx={{ minWidth: "50px", width: "50px", height: "50px" }}
                        onClick={() => editStation(station)}
                      >
                        <EditOutlinedIcon sx={{ position: "relative !important" }} />
                      </Button>
                    </div>
                  </Stack>
                </Card>

                <Stack direction="row-reverse" justifyContent="space-between" alignItems="center" px={1.5} py={0.5}>
                  <Typography className="fa-number" sx={{ direction: "ltr", mb: "0 !important" }}>
                    {station.paths.length} مسیر
                  </Typography>
                  <Typography sx={{ mb: "0 !important" }}>ایستگاه {stationData.text}</Typography>
                </Stack>
              </Card>
            </Popup>
          )}
        </Marker>
      );
    });
  }, [stations, type, ids, visibility]);

  return (
    <>
      {" "}
      <Pane style={{ zIndex: stationLayer }} name="show-stations" key={stationLayer}>
        {stationsMarker}
      </Pane>
    </>
  );
}
